import React, { useEffect, useRef } from 'react';
import WizardStepIndicator from '../WizardStepIndicator';
import type { WizardStepInfo } from '../WizardStepIndicator';
import WizardNavigation from '../WizardNavigation';
import type { WizardNavLabels } from '../WizardNavigation';

interface WizardShellProps {
  headerContent?: React.ReactNode;

  // Step indicator
  steps: WizardStepInfo[];
  currentStep: number;
  visitedSteps: boolean[];
  onStepClick: (index: number) => void;

  // Content
  children: React.ReactNode;

  // Navigation
  totalSteps: number;
  canGoNext: boolean;
  canGoPrevious: boolean;
  isLastStep: boolean;
  isSubmitting: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onFinish: () => void;
  onCancel: () => void;
  navLabels?: WizardNavLabels;
}

const WizardShell: React.FC<Readonly<WizardShellProps>> = ({
  headerContent,
  steps,
  currentStep,
  visitedSteps,
  onStepClick,
  children,
  totalSteps,
  canGoNext,
  canGoPrevious,
  isLastStep,
  isSubmitting,
  onPrevious,
  onNext,
  onFinish,
  onCancel,
  navLabels,
}) => {
  // .wizard-content is the scroll container (overflow-y: auto); without this,
  // a step change keeps the previous step's scroll offset and the next step
  // opens mid-page.
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, [currentStep]);

  return (
    <div className="wizard-container">
      {headerContent}

      <WizardStepIndicator
        steps={steps}
        currentStep={currentStep}
        visitedSteps={visitedSteps}
        onStepClick={onStepClick}
      />

      <div className="wizard-content" ref={contentRef}>{children}</div>

      <WizardNavigation
        currentStep={currentStep}
        totalSteps={totalSteps}
        canGoNext={canGoNext}
        canGoPrevious={canGoPrevious}
        isLastStep={isLastStep}
        isSubmitting={isSubmitting}
        onPrevious={onPrevious}
        onNext={onNext}
        onFinish={onFinish}
        onCancel={onCancel}
        labels={navLabels}
      />
    </div>
  );
};

export default WizardShell;
